import { useCallback, useState } from "react";
import { Badge } from "./badge";
import { Button } from "./button";
import { Input } from "./input";
import { X } from "lucide-react";
import { Typography } from "./Typography";
import { cn } from "../lib/utils";
import { EMAIL_REGEX } from "@cointervue/shared";

type MultiInputProps = {
  id?: string;
  label?: string;
  value?: string[];
  onChange?: (values: string[]) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  invalidEmailError?: string;
  duplicateEmailError?: string;
};

export function CustomMultiBadgeInput({
  id,
  label,
  value = [],
  onChange,
  placeholder = "Type and press Enter",
  error: externalError,
  disabled = false,
  readOnly = false,
  className = "",
  invalidEmailError = "Please enter a valid email address",
  duplicateEmailError = "This email already exists",
}: MultiInputProps) {
  const [input, setInput] = useState("");
  const [internalError, setInternalError] = useState("");

  const error = externalError || internalError;

  const addValue = useCallback(() => {
    if (disabled || readOnly) return;
    const trimmed = input.trim();
    if (!trimmed) return;

    if (!EMAIL_REGEX.test(trimmed)) {
      setInternalError(invalidEmailError);
      return;
    }

    const isDuplicate = value.some(
      (v) => v.toLowerCase() === trimmed.toLowerCase(),
    );

    if (isDuplicate) {
      setInternalError(duplicateEmailError);
      return;
    }

    const updated = [...value, trimmed];
    onChange?.(updated);
    setInput("");
    setInternalError("");
  }, [
    input,
    value,
    onChange,
    disabled,
    readOnly,
    invalidEmailError,
    duplicateEmailError,
  ]);

  const removeValue = useCallback(
    (item: string) => {
      if (disabled || readOnly) return;
      onChange?.(value.filter((v) => v !== item));
    },
    [onChange, value, disabled, readOnly],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInput(e.target.value);
      if (internalError) setInternalError("");
    },
    [internalError],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled || readOnly) return;
      if (e.key === "Enter") {
        e.preventDefault();
        addValue();
      } else if (e.key === "Backspace" && input === "" && value.length > 0) {
        removeValue(value[value.length - 1]);
      }
    },
    [input, value, addValue, removeValue, disabled, readOnly],
  );

  return (
    <div className={className}>
      {label && (
        <Typography as="label" htmlFor={id} className="mb-1 block">
          {label}
        </Typography>
      )}
      <div
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-2 rounded-md border bg-background px-3 py-2",
          error && "border-destructive",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        {value.map((item) => (
          <Badge key={item} variant="secondary" className="shrink-0 gap-1 normal-case">
            {item}
            {!readOnly && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0"
                onClick={() => removeValue(item)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </Badge>
        ))}

        <Input
          id={id}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          className="min-w-[120px] flex-1 border-none p-0 focus-visible:ring-0"
        />
      </div>
      {error && <Typography variant="error"> {error}</Typography>}
    </div>
  );
}
